import {makeAutoObservable} from "mobx";
import {environment} from "../env";
import type {DashboardCard} from "../components/dashboard/DashboardCardConfigs.tsx";
import {CARD_CONFIG} from "../components/dashboard/DashboardCardConfigs.tsx";
import moment from "moment";
import type {HierarchicalSankeyOptions} from "../components/visualization/sankey/HierarchicalSankeyChart";
import type {CurrencyAbbreviation} from "../components/currency-component/CurrencyClasses";
import type {MatKpiTreeValues} from "../services/ApiTypes";
import type {SearchConfiguration, SortingConfiguration} from "../customization/SearchConfiguration";
import type {ColSpec} from "../components/table/MithraTableHeadColumns";
import {C} from "../configurations/active-configuration";
import type {FilterConfiguration} from "../customization/FilterConfiguration";
import type {Range} from "../utils/ts-utils";
import type {Bag} from "../services/classes/Bag";
import type {MultiTypedSearchOptionsSpecification} from "../components/search/MultiTypedSearchManager";
import type {PpvColumns} from "../jobs/koi/purchase-price-variance/classes/PpvApiClasses";
import dayjs from "dayjs";
import objectSupport from "dayjs/plugin/objectSupport";

import "dayjs/locale/nl.js";
import "dayjs/locale/fr-ch.js";
import "dayjs/locale/en-gb.js";
import "dayjs/locale/en.js"
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {getDayJsLocale} from "../utils/day-js-utils.ts";
import type {
    CustomReviewColumnTypes
} from "~/pages/categorization/components/review-table/review-table-muix/columns/ColumnTypes";
import type {JSONSerializableColumnType} from "../components/muix-data-grid/cells/MuiXBasicColumnTypes.ts";
import type {DashboardSection} from "~/components/dashboard/DashboardSectionConfig.tsx";

dayjs.extend(objectSupport)

const P_CONTEXTS = ['1', '2', '3'] as const;
type CONTEXT_TYPES = typeof P_CONTEXTS[number];

type ContextFieldName = `p_context_${CONTEXT_TYPES}` | `s_context_${CONTEXT_TYPES}`
type ContextColName = `${ContextFieldName}_col_name`
    | `${ContextFieldName}_col_type`

// type ContextColName = `${ContextFieldName}_col_name` | `${ContextFieldName}_col_type`;

type DATE_COLUMN_TYPES = CONTEXT_TYPES;
type DateContextFieldName = `p_ddate_${DATE_COLUMN_TYPES}` | `p_date_${DATE_COLUMN_TYPES}`
type DateContextColName = `${DateContextFieldName}_col_name` | `${DateContextFieldName}_col_type`;

export type CompanyProfile = {
    [key in (
        ContextColName |
        DateContextColName
    )]?: string;
} & {
    p_name_col_name?: string | false
    p_description_col_name?: string | false
    s_col_name?: string | false
    bu_col_name?: string | false
    c_description_col_name?: string | false
    cc_description_col_name?: string | false
    /**
     * Show the business units in the Review page
     * TODO CAT-1324: This must be depending on the data availability, not a switch in the FE
     * @deprecated Business Unit will always be shown (the user can hide it if needed)
     * Replace this setting with bu-hidden by default
     */
    show_bu_name_in_review_pages?: true
    /**
     * The preference of the context columns, if desired
     */
    p_context_pref?: Range<1, 4>[]
    /**
     * Other partContextColNames that should be shown
     */
    defaultPartContextColNames?: ContextColum[]
    /**
     * The preference of the d_date columns, if desired
     */
    p_ddate_pref?: number[]
    /**
     * Column width for specific attributes
     */
    s_name_col_width?: number
    p_name_col_width?: number
    p_name_col_min_width?: number
    /**
     * The maximal size of the p_name column before we use "..."
     */
    p_name_ellipsis_cutoff?: number
    /**
     * The maximal size of the taxonomy category before we use "..." in categorization page. Should be an even number
     * Overrides env variable with same name if set
     */
    categoryNameCutoffEllipsisChars?: number
    p_ddate_1_col_width?: number
    previous_cat_col_width?: number
    cat_col_width?: number
    p_bu_name_col_width?: number
    // TODO CAT-3066 these configuration keys are not used in MUIX table, remove after removal of old categorization component
    p_description_col_width?: number
    p_description_col_min_width?: number
    p_description_col_class?: string
    sortingConfiguration?: SortingConfiguration
    /**
     * Filter capability in the categorization review screen
     */
    filterConfiguration?: FilterConfiguration
    /**
     * The type of Googler search that should be used
     */
    searchConfiguration?: SearchConfiguration

    /**
     * TODO CAT-1649: This is hard to maintain and evolve, we should make this more generic
     */
    catFilterSearchAlsoPC3?: true

    /**
     * Enable Explore Categories card when databag is sample
     */
    enableExploreCategoriesOnSample?: true
    /**
     * Enable Advanced Dashboard card when databag is sample
     */
    enableAdvancedDashboardOnSample?: true
    /**
     * Enable Spend Concentration card when databag is sample
     */
    enableSpendConcentrationOnSample?: true
    /**
     * Enable Chat when databag is sample
     */
    enableChatOnSample?: true

    /**
     * Enable golden record card
     */
    enableGoldenRecord?: true
    enableGoldenRecordBar?: true

    /**
     * When using a separate file for the taxonomy, only show the number of categories
     */
    taxonomy_builder_only_n_cats?: boolean

    /**
     * The nth-smallest number that should be visible, in the domain [0, 1]
     * i.e. for graph sizes [0, 10, 20] the 66% visibility means: number 10 should be at least visible (20px high)
     * Higher values lead to smaller diagrams
     */
    sankeyVisibilityFraction?: number

    sankeyMinHeight?: number
    sankeyMaxHeight?: number

    /**
     * If the taxonomy is particular deep
     * (Leaves more space for where we show the taxonomy categories)
     */
    deepTaxonomy?: true

    /**
     * Show the category_id in the categorization review table
     */
    showCategoryId?: true

    taxonomyBuilderHeight?: number

    /**
     * Note: This is BROKEN since move to materialized backend
     */
    allowReviewDownloadExcel?: boolean
    allowReviewDownloadCsv?: boolean

    locale: string
    currency: string

    /**
     * We are not able to link a single taxonomy to multiple bags.
     * For this reason we hardcode the taxonomy in the frontend. CAT-353
     */
    hardcodeTaxonomyId?: number

    hardcodedKPIAbbreviation?: CurrencyAbbreviation

    /**
     * Allow the user to send for approval of a taxonomy which is not filled completely to it's deepest level
     *
     * This is a specific switch because we currently do not support categorization at variable depth.
     * As well we did not test how such a categorization looks, nor did the data model evolve to support this.
     * So we specifically enable it, on customer basis.
     */
    allowIncompleteTaxonomy?: boolean

    /**
     * Hide repeating labels in the categorization review page
     *
     * Note: We should revise the taxonomy definition to avoid repeating labels being send to the FE/AI
     */
    hideRepeatingLabels?: boolean

    /**
     * Hide the send for approval buttons, (For demonstration builds only)
     */
    hideApprovalButtons?: true

    /**
     * A specific master bag to be used
     * TODO: Remove this when updating the demo
     */
    hardcodeMasterBagId?: number
    hardcodedBagId?: number
    hardcodedCategorizationBagId?: number
    hardcodedCombinedBagId?: number
    hardcodedBaseTaxonomyId?: number
    hardcodedSubsidiaryBagId?: number
    hardcodedTaxonomySuggestionId?: number
    hardcodedOriginalTaxonomyId?: number

    /**
     * A specific master taxonomy to be used to merge datasets
     */
    masterTaxonomyId?: number

    /**
     * Use the modified ATK synergy dashboard
     * @deprecated TODO: Replace this with different deployment configuration
     */
    new_synergy_dashboard?: boolean

    /**
     * Enable the displaying of databags in the table of the Synergy dashboard
     * Note: The names of the databag are trimmed by "(...)"
     */
    synergyDashboardShowDatabagName?: true

    /**
     * Enable the business units in the advanced dashboard
     * Note: Should be moved to the BE
     * Note: Not included in the automatic cache population
     */
    synergyDashboardShowBusinessUnits?: true

    /**
     * Enable the c_description filter in the advanced dashboard
     * WARNING: This field is grouped_by anyways, so we might show duplicate rows if this data is filled in at the BE
     * Note: Should be moved to the BE
     */
    synergyDashboardShowCDescriptionFilter?: true

    /**
     * Enable the sap_plant_description filter in the advanced dashboard
     * WARNING: This field is grouped_by anyways, so we might show duplicate rows if this data is filled in at the BE
     * Note: Should be moved to the BE
     */
    synergyDashboardShowPlantFilter?: true

    /**
     * Enable the c_address filter in the advanced dashboard
     * WARNING: This field is grouped_by anyways, so we might show duplicate rows if this data is filled in, at the BE
     * Note: Should be moved to the BE
     */
    synergyDashboardShowCAddressFilter?: true

    /**
     * Enable the Supplier.geo_region filter in the advanced dashboard
     * Note: This is still experimental, however we do not show it if there is no data in the BE. So this option can be removed
     */
    advancedDashboardShowGeoRegionFilter?: true

    /**
     * Enable split functionality for advanced/opportunity/synergy dashboard
     * This condition will have effect if synergy relation api returns up to 4 sub-databags
     */
    synergyDashboardAllowSplit?: true

    hardcodedSynergyId?: number

    /**
     * view only option for the taxonomy builder
     *
     * For when we want the customer to not change the taxonomy anymore
     */
    taxonomyBuilderViewOnly?: boolean

    /**
     * By default, this is disabled
     */
    enableSankey?: boolean

    /**
     * Show the experimental opportunity tracker
     */
    enableOpportunityTracker?: boolean

    /**
     * Show the Business Unit Selector in the review page or not:
     * - Enabled the breadcrums and first BU filter page
     */
    enableBuFilter?: boolean

    /**
     * Show the Business Unit Selector in the review page for sample databags when landing in the categorization page.
     *
     * Normally we exclude this filter as the sample is quite small. Some customers do need this filter on the sample
     */
    gotoBuFilterInSample?: true

    hideBuInCategorizationTable?: boolean

    hideDateInCategorizationTable?: true

    showExportInCategorizationTable?: true

    ppvDemo?: true;

    disableRunningOfModels?: boolean;
    masterCompanyName?: string;
    compareCompanyName?: string;
    showResultPills?: boolean;

    /**
     * Show the score by default
     */
    categorizationShowScore?: undefined | true | false;

    categorizationShowFileDownload?: undefined | true;

    /**
     * CAT-968: Add a hack to disable showing uncategorized
     */
    hackHideUncategorizedInReview?: true;

    /**
     * Show data ingestion in main flow of the app (demo only)
     */
    enableDataIngestion?: true;

    demoFakeDataIngestion?: true;
    demoFakeDataIngestionTarget?: {
        databagId: number
    };

    /**
     Show taxonomy ingestion in main flow of the app (demo only)
     */
    enableTaxonomyIngestion?: true;

    /**
     * Switch for turning SIT on or off
     */
    enableSavingInitiativeTracker?: boolean;
    sitHideSkuForm?: true;

    /**
     * Switch for turning ai suggestions in taxonomy builder on or off
     */
    useAiTaxonomySuggestion?: true;
    /**
     Switch for turning Taxonomy Suggestions Demo in taxonomy builder on or off
     * (Initially for Danone)
     */
    demoFakeTaxonomySuggestion?: true
    /**
     * Switch for turning ai health check in taxonomy builder on or off
     */
    useAiTaxonomyHealthCheck?: boolean;
    /**
     * Switch for turning the parent supplier in the advanced dashboard on or off
     * TODO: This must be depending on the data statistics, not a switch
     */
    hideParentSupplierOpportunityDashboard?: true;
    /**
     * Switch for turning the L4 category on or off in the advanced dashboard
     * TODO: This must be depending on the data statistics, not a switch
     */
    showL4InOpportunityDashboard?: true;

    /**
     * Enable the pre-loading of the advanced dashboard
     */
    disablePreloadAdvDashboard?: true;

    /**
     * Default value for common category filter in advanced dashboard is on
     */
    defaultCommonCategoryAdvDashboard?: true;
    /**
     * Disable the common category filter in advanced dashboard
     * TODO: This feature is still experimental and is vague for non-synergy oriented users
     */
    disableCommonCategoryAdvDashboard?: true;
    /**
     * Disable the common supplier filter in advanced dashboard
     * TODO: This feature is still experimental and is vague for non-synergy oriented users
     */
    disableCommonSuppliersAdvDashboard?: true;
    /**
     * Default value for common suppliers filter in advanced dashboard is on
     * TODO: This must be COMMON instead of ParentSuppliers?
     */
    defaultCommonSuppliersAdvDashboard?: true;

    /**
     * Hide some example dummy opportunities in the opportunity tracker
     */
    hideHardcodedOpportunities?: true;

    /**
     * Cards that should only be shown as trial
     */
    disabledCards?:
        DashboardCard[]
        | {[key in DashboardCard]?: {msg: string}}

    /**
     * Cards that should not be shown
     */
    hideCards?: DashboardCard[]

    /**
     * Override to show specific experimental cards
     */
    showCards?: DashboardCard[]

    /**
     * TODO CAT-3862: Remove this hack
     * Show notification `Module only available for ${_dataset} ${_datasetNumbers}` when not in this array
     */
    contractCoverageOnlyForDatabag?: number[]

    /**
     * Show the sub bars in the categorization screen with uncategorized below it
     * (Only works well in equally distributed taxonomy)
     */
    showUncategorizedSubBars?: boolean

    /**
     * If the results are created beforehand, we can disable the supplier normalization model
     * We also skip the Ai Supplier Normalization model while we are still developing the AI integration
     * This is mostly used for demonstration purposes
     */
    skipAiSupplierNormalizationModel?: boolean

    /**
     * Disable the supplier normalization AI integration
     * While the AI integration is still in development, we can disable it here
     * At the time of writing this should be set for almost all environments, except the demo's
     *
     * @see skipAiSupplierNormalizationModel
     */
    disableSupplierNormalizationAiModel?: true

    /**
     * TODO: Ad hoc switch for CAT-969
     * Hide KPI's of supplier normalization with very low values
     */
    hideSmallStandaloneSpend?: {
        minStandaloneSpend: number,
        minStandaloneSuppliers: number
    }

    /**
     * Demonstrate the round trips for AI
     */
    aiRoundTripDemo?: true

    /**
     * When the results are created beforehand, and we do not allow the user to run the model from the FE
     */
    skipAiTaxonomyMapperModel?: true
    /**
     * TODO: CAT-888: Remove this hack
     */
    hackNSuppliersFromCommonSuppliers?: true
    lookerUrls?: string[]
    displaySynergyOverview?: boolean
    hardcodedDatasetNameLeft?: string
    hardcodedDatasetNameRight?: string
    hardcodedNumberTotalSuppliersLeft?: number
    hardcodedNumberTotalSuppliersRight?: number
    hardcodedNumberTotalCommonSuppliers?: number
    hardcodedTotalSpendCommonSuppliers?: number
    hardcodedNumberCommonSuppliersLeft?: number
    hardcodedNumberCommonSuppliersRight?: number
    hardcodedTotalSuppliersSpendLeft?: number
    hardcodedTotalSuppliersSpendRight?: number
    hardcodedCommonSuppliersSpendLeft?: number
    hardcodedCommonSuppliersSpendRight?: number
    hardcodedNumberTotalCategoriesLeft?: number
    hardcodedNumberTotalCategoriesRight?: number
    hardcodedNumberTotalCommonCategories?: number
    hardcodedNumberCommonCategoriesLeft?: number
    hardcodedNumberCommonCategoriesRight?: number
    hardcodedTotalSpendCommonCategories?: number
    hardcodedTotalCategoriesSpendLeft?: number
    hardcodedTotalCategoriesSpendRight?: number
    hardcodedCommonCategoriesSpendLeft?: number
    hardcodedCommonCategoriesSpendRight?: number

    // hackCategorizationUseLatestCategories?: true // > Changes to the taxonomy always need to go though the review

    // This two values are used to determine on with taxonomy to hide the error for missing L while sending for approval
    hackTaxonomyToHideApprovalError?: number
    readOnlySuggestion?: boolean
    hideLinksUserDropDownMenu?: boolean
    /**
     * Use this field to specify relation data is to be displayed in the categorization review table
     */
    categorizationReviewSubRowRelationData?: Array<CategorizationReviewSubRowRelationsData>
    /**
     * This hides the group by supplier function
     */
    hideGroupBySupplier?: true

    /**
     * Normal the default is determined by the type of databag:
     * - Sample: Not grouped
     * - Normal: Grouped
     *
     * Set to override the default behavior and always show the categorization review in a non-grouped way
     *
     * TODO: CAT-2838 (after cat-2729-mui-x-data-grid branch is merged)
     *
     * Useful for example when the databag is quite small, like a sample.
     *  There are also more filtering capabilities in the part screen
     */
    initialGroupBySupplierInactive?: boolean,

    ppvTablePageSize?: number
    ppvSubTablePageSize?: number

    /**
     * TODO: Should be based on (1) available data, (2) PPV config, (3) user analysis setting
     * There should be some required columns as well like: part, supplier
     * By default this is PpvApiClasses.PPV_COLUMN_OPTIONS
     */
    ppvConfigEnabledColumns?: PpvColumns[]

    /**
     * Switch to turn on the option to fake demo a categorization run
     */
    demoFakeReinforcedCategorization?: true
    demoFakeReinforcedCategorizationTarget?: {
        databagId: number,
        taxonomyId: number,
        taxonomySize: number
    }
    demoFakeSendTaxonomyForApprovalTarget?: {
        databagId: number,
        taxonomyId: number,
        taxonomySize: number
    }
    /**
     * Show the supplier_id in the Supplier Normalization page
     * TODO CAT-1324: This must be depending on the customers preferences and data upload choices, not a switch in the FE
     */
    showSupplierIdInSupplierNormalization?: true
    /**
     * Show the supplier_id in the Supplier Normalization page
     * TODO CAT-1324: This must be depending on the data availability, not a switch in the FE
     */
    showSupplierCountryInSupplierNormalization?: true
    /**
     * Show the business units in the Supplier Normalization page
     * TODO CAT-1324: This must be depending on the data availability, not a switch in the FE
     */
    showBusinessUnitInSupplierNormalization?: true
    /**
     * Show the s_vat in the Supplier Normalization page
     * TODO CAT-1324: This must be depending on the data availability, not a switch in the FE
     */
    showSupplierVatInSupplierNormalization?: true
    /**
     * Hack: cutoff the first n digits of the s_id when showing it to the user
     * TODO: Introduce a customer_bu_id field in the BE
     */
    showSupplierIdPrefixCutoffHack?:
        {type: 'by_substring', start: number}
        | {type: 'by_split', separator: string}

    /**
     * Hack: cutoff the first n digits of the bu_id when showing it to the user
     * TODO: Introduce a customer_bu_id field in the BE
     */
    showBuIdPrefixCutoffHack?: number
    /**
     * Switch to turn on the business case generator demo
     */
    demoFakeBusinessCaseGenerator?: true
    /**
     * Alternative name for parent supplier column in adv dashboard
     */
    parentSupplierAltName?: string
    /**
     * Add the s_id next to the supplier's name
     */
    showSupplierIdInAdvDashboard?: true
    /**
     * Add the s_id next to the supplier's name
     * Note: Ensure that categorizationReviewSubRowRelationData[supplier] is set as well!
     *
     * FIXME CAT-2308: It's too complicated to add the s_id to the grouped endpoint, so it's not implemented in the FE
     *  The s_id only shows up in the singular part table
     */
    showSupplierIdInCategorization?: true
    /**
     * Add bu_id next to the business unit's name
     */
    showBuWithId?: true
    /**
     * Hides the 'No Entity' filter in the bu selection categorization page
     */
    hideNoBuFilterInCategorization?: true
    /**
     * When the dataset has the specially names "missing" BU, we do not need to check for NULL bu's
     * TODO CAT-1324: This must be depending on the data availability, not a switch in the FE
     */
    datasetHasMissingBu?: true
    /**
     * Hides the supplier search in the advanced dashboard
     */
    disableAdvDashboardSearchBySupplier?: true

    disablePPVforDatabag?: number[]

    /**
     * TODO CAT-2498: Remove this hack
     */
    disablesSupplierNormalizationForDatabag?: number[]

    /**
     * Makes use of the Firestore architecture for the contract parser
     */
    hasFirestore?: true

    /**
     * Us the direct upload of cat-ingestion. Otherwise, we use the chunked upload of GCP
     */
    useIngestionDirectUpload?: true

    /**
     * Show reason column in the categorization review table
     * Default: true
     */
    showReasonColumn?: true | false

    /**
     * Auto update review statistics after each review is submitted.
     */
    autoUpdateReviewStats?: true

    /**
     * Enable the AI integration for the categorization review
     */
    enableVertexAiCategorizationIntegration?: true

    /**
     * Enable the AI integration for the categorization review for STAFF
     */
    enableVertexAiCategorizationIntegrationForStaff?: true

    /**
     * override categorizaion model name
     */
    overrideAiCategorizationPipelineName?: string

    /**
     * Configure the standalone filter on supplier normalization
     * - ALWAYS_HIDE_STANDALONE: No toggle, no standalone suppliers
     * - ALWAYS_SHOW_STANDALONE: No toggle, show also standalone suppliers
     * - TOGGLE_TO_SHOW_STANDALONE (default): With toggle, by default no standalone suppliers
     * - TOGGLE_TO_HIDE_STANDALONE: With toggle, by default we also show standalone suppliers
     *
     * See CAT-3590 before changing the default
     */
    supplierStandAloneFilter?: 'ALWAYS_HIDE_STANDALONE' | 'ALWAYS_SHOW_STANDALONE' | 'TOGGLE_TO_SHOW_STANDALONE' | 'TOGGLE_TO_HIDE_STANDALONE' | '(default)'

    /**
     * Show ai-proc-chat at dashboard
     */
    enableProcChat?: true;

    /** Contract Explorer: show extra part columns from the databag's
     *  columnConfig in the Transactions tab and the link picker.
     *  Off unless a customer opts in. */
    contractExplorerConfigColumns?: true

    /**
     * Allow filters to be stored in the browser
     * (experimental)
     */
    enableExperimentalStoreLocalFilter?: true

    /**
     * Enable the fake creation of opportunities in chat
     */
    enableChatOpportunityDemo?: true

    /**
     * Enable the backend status page link at the bottom of the page
     *
     * Defaults to ${B.MATERIALIZED_HOST}/server-status
     */
    enableBackendStatusPage?: true

    /**
     * In the backend we configure MITHRA_REVIEW_ALL_NODES if we want to allow to review with all categories from the taxonomy, not only leafs
     */

    /**
     * Branding options
     */
    branding?: 'NExO'
}

export const DEFAULT_SANKEY_VISIBILITY_FRACTION = .67;

function getCompanyProfile(): CompanyProfile {
    return C.profile;
}

type ContextColum = {
    colName: string,
    colType: string,
    dataField: string,
    contextI: CONTEXT_TYPES,
    colClassName: string
};

export type CategorizationReviewSubRowRelationsData = {
    relation: string,
    columns: Array<CategorizationReviewSubRowRelationData>
}

export type CategorizationReviewSubRowRelationData = {
    db_column: string
    colSpec: ColSpec | null
    // If you want to make column searchable, make sure that the related search column is also included in BE
    // See PART_REVIEW_SEARCH_OPTIONS in cat_material
    includeInSearch?: boolean
    serializableType?: JSONSerializableColumnType<CustomReviewColumnTypes>
}

/**
 * TODO: Retrieve this from the BE or compile it to hide it from other customers
 * - We want this to be available from the start to render the right page.
 * - We want to hide it between customers
 */
export default class ProfileStore {
    public p: CompanyProfile = getCompanyProfile()

    constructor() {
        makeAutoObservable(this)

        // TODO CAT-744: Replace moment with Luxon or dayJs

        // const locale = (window.navigator.userLanguage || window.navigator.language) || 'en-GB';
        // moment.locale(locale);
        // moment.locale('en-GB');

        // import 'moment/locale/nl';
        // import 'moment/locale/fa';

        moment.locale(this.p.locale);
        // console.log('moment.locales()', moment.locales())

        dayjs.locale(getDayJsLocale(this.p.locale)
            // Ensure the AdapterDayjs is imported and compiled when dayJs is loaded
            + `${dayjs}${AdapterDayjs}`.slice(0, 0)
        );

    }

    /**
     * Show or not show a card
     */
    showCard(card: DashboardCard): boolean {
        const config = CARD_CONFIG[card];

        if (this.p.hideCards?.includes(card)) {
            // Card is explicitly hidden
            return false;
        }

        let visible = !config.defaultHidden;

        // In sales demo everything is shown by default
        visible ||= environment.package === 'sales_demo';

        // Check if it's explicitly shown
        visible ||= Boolean(this.p.showCards?.includes(card));

        // Show cards based on other profile settings
        switch (card) {
            case 'data-ingestion':
                visible ||= Boolean(this.p.enableDataIngestion);
                break
            case 'taxonomy-ingestion':
                visible ||= Boolean(this.p.enableTaxonomyIngestion);
                break
            case 'chat':
                visible ||= Boolean(this.p.enableProcChat);
                break
            case 'golden-record':
                visible ||= Boolean(this.p.enableGoldenRecord);
                break
            case 'sit':
                visible ||= Boolean(this.p.enableSavingInitiativeTracker);
                break
        }

        return visible;
    }

    /**
     * Show a card as trial/disabled
     */
    isDisabledCard(card: DashboardCard, bag: Bag | undefined): {disabled: boolean, message?: string} {
        let message: undefined | string = undefined;
        let disabled = false;
        const isSample = Boolean(bag?.is_sample_of);

        // Check if card is explicitly disabled
        const disabledCards = this.p.disabledCards;
        if (disabledCards) {
            if (Array.isArray(disabledCards)) {
                if (disabledCards.includes(card)) {
                    // Card is explicitly disabled
                    disabled = true;
                }
            } else if (disabledCards[card]) {
                // Card is explicitly disabled, with a message
                disabled = true;
                message = disabledCards[card].msg;
            }
        }

        if (!disabled) {
            // Check specific app's
            if (this.p.disablePPVforDatabag && bag && card === 'koi-ppv') {
                disabled ||= Boolean(this.p.disablePPVforDatabag.includes(bag.id));
            }
            if (this.p.disablesSupplierNormalizationForDatabag && bag && card === 'supplier-normalization') {
                disabled ||= Boolean(this.p.disablesSupplierNormalizationForDatabag.includes(bag.id));
            }
            if (this.p.contractCoverageOnlyForDatabag && bag && card === 'contract-coverage-intelligence') {
                if (!this.p.contractCoverageOnlyForDatabag.includes(bag.id)) {
                    disabled ||= true;
                    const _dataset = this.p.contractCoverageOnlyForDatabag.length === 1 ? 'dataset' : 'datasets'
                    const _datasetNumbers = this.p.contractCoverageOnlyForDatabag.join(', ')
                    message = `Module only available for ${_dataset} ${_datasetNumbers}`
                }
            }
        }

        if (!disabled) {
            // Check if the card is disabled because of other reasons
            const config = CARD_CONFIG[card];

            if (config.sampleDisabled && bag && isSample) {
                switch (card) {
                    case 'adv-dashboard':
                        disabled = !this.p.enableAdvancedDashboardOnSample;
                        break;
                    case 'koi-spend-concentration':
                        disabled = !this.p.enableSpendConcentrationOnSample;
                        break;
                    case 'koi-key-data-facts':
                        disabled = !this.p.enableExploreCategoriesOnSample;
                        break;
                    case 'chat':
                        disabled = !this.p.enableChatOnSample;
                        break;
                    default:
                        disabled = true;
                }
                if (disabled)
                    message = 'This feature is not available for sample datasets';
            }
        }

        if (!disabled && environment.isDemo) {
            const DISABLED_DEMO = {
                99101: ['supplier-normalization'],  // ERP1
                99102: [''],  // ERP2
                99103: [''], // ERP3
                99100: ['supplier-normalization'], // ERP4 LO
                30: ['koi-ppv', 'supplier-normalization'], // ERP5 SR
                99110: [''], // Anonymized (Combined)
                17: [], // ERP6 A
            }
            if (bag?.id && DISABLED_DEMO[bag.id]?.includes(card)) {
                disabled = true;
                message = 'This is a demo environment';
            }
        }
        return {disabled, message};
    }

    /**
     * Get all visible cards group per dashboard section
     */
    getVisibleCardsBySection(): Record<DashboardSection, DashboardCard[]> {
        const result: Record<DashboardSection, DashboardCard[]> = {
            sit: [],
            chat: [],
            data_1: [],
            insights_2: [],
            no_section: [],
        };

        Object.keys(CARD_CONFIG).forEach(card => {
            if (!this.showCard(card as DashboardCard)) {
                return;
            }
            const section = CARD_CONFIG[card as DashboardCard].section;
            result[section].push(card as DashboardCard);
        });

        Object.values(result).forEach(cards => cards.sort((a, b) => CARD_CONFIG[a].ordering - CARD_CONFIG[b].ordering));
        return result;
    }

    /**
     * Return the context column names
     * @returns {colName, colType, dataField, contextI}[]
     */
    get partContextColNames(): ContextColum[] {
        const result: ContextColum[] = this.p.defaultPartContextColNames ? [...this.p.defaultPartContextColNames] : []
        const contexts = this.p.p_context_pref || P_CONTEXTS
        for (const iC of contexts) {
            const C = String(iC) as CONTEXT_TYPES;
            const colName = this.p[`p_context_${C}_col_name`];
            if (colName === undefined) {
                return result;
            } else {
                const dataField = `p_context_${C}`
                const colType = this.p[`p_context_${C}_col_type`] || 'any';
                const colClassName = `col-${dataField}`
                    + (colType === 'any' ? '' : ` col-type-${colType}`)
                result.push({colName, colType, dataField, contextI: C, colClassName})
            }
        }
        return result;
    }

    filterLastName(userLastName: string) {
        if (userLastName.toLowerCase() === environment.customerName.toLowerCase()) {
            // Filter last names with the same name as the company
            return ''
        }
        return userLastName;
    }

    get currencyFormat(): Intl.NumberFormat {
        return new Intl.NumberFormat(this.p.locale, {style: 'currency', currency: this.p.currency});
    }

    get currencySymbol(): string {
        return (0).toLocaleString(this.p.locale, {
            style: 'currency',
            currency: this.p.currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).replace(/\d/g, '').trim();
    }

    getCategoryViewConfig(selectedKpi: MatKpiTreeValues, nLinks: number, topLinkValue: number): Partial<HierarchicalSankeyOptions> {
        if (nLinks === 0) {
            return {};
        }
        const options: Partial<HierarchicalSankeyOptions> = {
            minValueForLabel: 0,
        }
        if (environment.customerName === 'vion') {
            // TODO: CAT-712
            // const zoom = 1.8
            // options.width = 1920 / zoom
            // options.height = 880 / zoom
            // options.margin.top = 50
            // options.margin.bottom = 10
            // options.margin.left = 100 + options.width / 6
            // options.margin.right = 100 + options.width / 6
            if (selectedKpi === 'parts' && topLinkValue >= 95255 / 2) {
                options.minValueForLabel = 4300;
            }
            if (selectedKpi === 'spend' && topLinkValue >= 715617252 / 2) {
                options.minValueForLabel = 4300;
            }
        }
        if (environment.customerName === 'franke') {
            // TODO: CAT-712
            options.margin = {
                left: 180,
                right: 80,
                top: 5,
                bottom: 5,
            }
            if (selectedKpi === 'parts') {
                options.minValueForLabel = 800;
            }
            if (selectedKpi === 'spend') {
                options.minValueForLabel = 6000000;
            }
        }

        return options;
    }

    get partDataName() {
        let pNameColName = this.p.p_name_col_name;
        if (pNameColName === false) {
            // disabled
            return '';
        }
        if (pNameColName && pNameColName.toLowerCase() === 'name') {
            // When it's simply called name then we have no specific name for this data type
            pNameColName = ''
        }
        return pNameColName || 'Transaction'
    }

    get partDataDescription() {
        return this.p.p_description_col_name || 'Description'
    }

    get supplierDataName() {
        return this.p.s_col_name || 'Supplier'
    }

    get parentSupplierDataName() {
        return this.p.parentSupplierAltName || 'Parent Supplier'
    }

    get supplierIdDataName() {
        return `${this.supplierDataName} ID`
    }

    get buDataName() {
        return this.p.bu_col_name || 'Business Unit'
    }

    get regionDataLabel() {
        return 'Region'
    }

    get groupKeyDataName() {
        return this.partDataName;
    }

    get ccDescriptionDataName() {
        return this.p.cc_description_col_name || 'Company Code Description';
    }

    get cDescriptionDataName() {
        return this.p.c_description_col_name || 'Company';
    }

    get plantDataName(): string {
        const col = (
            this.p.categorizationReviewSubRowRelationData?.find(r => r.relation === 'part')?.columns.find(c => c.db_column === 'sap_plant_description')
        )
        return col?.colSpec?.txt || 'Plant';
    }

    get supplierSearchSpecification(): MultiTypedSearchOptionsSpecification {
        // TODO: Not used anymore???
        return {
            defaultIndices: [0],
            options: [
                {key: 's_name', label: this.supplierDataName},
            ],
        }
    }

    isColumnSortable(dataField: ColSpec['columnFieldId'] | undefined): boolean {
        // CAT-722: This feature is not needed
        switch (this.p.sortingConfiguration) {
            case 'by_default':
                switch (dataField) {
                    case 'p_spend':
                    case 'p_suggestion_score':
                    case 'p_ddate_1':
                        return true;
                    default:
                        return false;
                }
            case 'by_default_and_c1':
                switch (dataField) {
                    case 'p_spend':
                    case 'p_suggestion_score':
                    case 'p_context_1':
                    case 'p_ddate_1':
                        return true;
                    default:
                        return false;
                }
            default:
                return false;
        }
    }

    categorizationShowScore() {
        if (environment.isTestReviewPage) return true;
        if (this.p.categorizationShowScore !== undefined) {
            return Boolean(this.p.categorizationShowScore);
        }
        return true;
    }

    /**
     * Use the AI-Round-Trip flow for testing
     */
    isVertexCategorizationEnabled(isStaff: boolean) {
        return Boolean(
            // Vertex AI integration is enabled
            this.p.enableVertexAiCategorizationIntegration
            ||
            // Vertex AI integration is enabled
            (isStaff && this.p.enableVertexAiCategorizationIntegrationForStaff)
            ||
            // Or the demo is enabled
            (
                // aiRoundTripDemo is enabled
                (// The environment is deployed as staging, or it's running locally
                    (this.p.aiRoundTripDemo
                        // demoFakeReinforcedCategorization is disabled
                        && !this.p.demoFakeReinforcedCategorization && (environment.isStaging || !environment.production)))
            )
        );
    }

    get opportunitySplitAllowed() {
        return Boolean(this.p.synergyDashboardAllowSplit);
    }

    static hasSpec(data: CategorizationReviewSubRowRelationsData[] | undefined, relation: string, db_column: string): boolean {
        return !!data?.some(r => r.relation === relation && r.columns.some(c => c.db_column === db_column && c.colSpec));
    }

    /**
     * Make a deep copy and add based on the `relation` and `db_column`
     */
    static addRelationData(data: CategorizationReviewSubRowRelationsData[], toAdd: CategorizationReviewSubRowRelationsData): void {
        const m = data.find(d => d.relation === toAdd.relation);
        if (!m) {
            data.push(toAdd);
        } else {
            toAdd.columns.forEach(col => {
                const c = m.columns.find(c => c.db_column === col.db_column);
                if (!c) {
                    m.columns.push(col);
                } else {
                    // Already exists, update it
                    Object.assign(c, col);
                }
            })
        }
    }

    isCategorizationShowFileDownloadHack(bagId: number | undefined) {
        // Magic databag for demo purpose only
        return environment.isDemo && bagId === 17;
    }

    get getSupplierStandAloneFilter(): Exclude<NonNullable<CompanyProfile['supplierStandAloneFilter']>, '(default)'> {
        if (!this.p.supplierStandAloneFilter || this.p.supplierStandAloneFilter === '(default)') {
            return 'TOGGLE_TO_SHOW_STANDALONE';
        }
        return this.p.supplierStandAloneFilter;
    }
}
